import { Component, DestroyRef, inject, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AddProject } from './add-project/add-project';
import { AddNewClient } from './add-new-client/add-new-client';
import { RebarAuthService } from '../../core/rebarauth/rebar.auth.service';
import { ReviewerService } from '../../core/services/reviewer.service';
import { MasterService } from '../../core/services/master.service';
import { DateFormatService } from '../../core/services/date-format.service';
import { PeoplePickerComponent } from '../../core/components/people-picker/people-picker.component';
import { GLRR_FIELD_CONFIG, QUARTERS, QUARTER_MONTH_MAP, CLIENT_QUALITY_OPTIONS, MonthOption, Quarter, ClientQuality } from './standard.constants';
import { CommonModule } from '@angular/common';
import { ProposedYearOption, CountryOption, CityOption, ProjectDetail, LocationReviewCard, StandardReviewForm, FormFieldKey, ValidationRule, ReviewDocument, createDefaultStandardReviewForm } from './standard.interfaces';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-standard-review',
  standalone: true,
  imports: [FormsModule, RouterLink, CommonModule, PeoplePickerComponent, MatTooltipModule],
  templateUrl: './standard-review.html',
  styleUrl: './standard-review.css',
})
export class StandardReview implements OnInit {
  private destroyRef = inject(DestroyRef);
  private dateFormatSvc = inject(DateFormatService);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;

  constructor(
    private router: Router,
    private rebarAuth: RebarAuthService,
    private reviewerService: ReviewerService,
    private masterService: MasterService
  ) { }

  reviewTypeCode = 'STD';
  reviewTypeId = 1;
  reviewTypeName = 'Standard Review';

  get reviewTypeLabel(): string {
    return this.reviewTypeName.replace(/^Go\s+Live Review$/i, 'Go-Live Review');
  }
  CreatedBy: string = '';
  CreatedDate: string = '';
  UpdatedBy: string = '';
  UpdatedDate: string = '';
  isLoading = false;
  showErrorPopup = false;
  errorPopupMessages: string[] = [];
  showCancelConfirm = false;

  locationCards: LocationReviewCard[] = [{
    LocationCity: '', LocationCountry: '', Quarter: '', ProposedMonth: '',
    RCManager: '', RCSupervisor: '', RCReviewer: '',
    SecondaryManager: '', SecondarySupervisor: '', SecondaryReviewer: '',
    IsParentLocation: true, ParentReviewerName: '',
  }];
  locationCardErrors: Array<{
    country?: string; city?: string; duplicate?: string;
    rcManager?: string; rcSupervisor?: string; rcReviewer?: string;
    manager?: string; supervisor?: string; reviewer?: string;
  }> = [{}];

  cardCities: CityOption[][] = [[]];
  cardCountryDropdownOpenIdx: number | null = null;
  cardCityDropdownOpenIdx: number | null = null;
  cardCountrySearchTerms: string[] = [''];
  cardCitySearchTerms: string[] = [''];
  private existingReviewNames = new Set<string>();
  reviewNameDuplicateError = '';
  clientAccountLeadSelected = false;
  operationAccountLeadSelected = false;
  clientAccountLeadError = '';
  operationAccountLeadError = '';
  attachedFiles: File[] = [];

  // Attachment validation: only these extensions are allowed, each file max 20 MB.
  readonly allowedFileExtensions = ['pdf', 'msg', 'xls', 'xlsx', 'doc', 'docx', 'txt', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'zip'];
  readonly maxFileSizeBytes = 20 * 1024 * 1024;
  attachmentErrors: string[] = [];
  proposedYears: ProposedYearOption[] = [];
  availableCities: CityOption[] = [];
  private citiesByCountryMasterID: Record<number, CityOption[]> = {};
  projectDetails: ProjectDetail[] = [];
  masterClientNames: string[] = [];
  dtProjectNames: string[] = [];
  contractNumbers: string[] = [];
  deliveryCountries: CountryOption[] = [];

  /** All team members loaded from the API; filtered per role into supervisor / reviewer / manager dropdowns. */
  allUsers: { label: string; value: string; role: string }[] = [];

  supervisorDropdownKey: string | null = null;
  supervisorSearchTerm = '';
  reviewerDropdownKey: string | null = null;
  reviewerSearchTerm = '';
  managerDropdownKey: string | null = null;
  managerSearchTerm = '';

  clientSearchTerm = '';
  clientDropdownOpen = false;
  projectSearchTerm = '';
  projectDropdownOpen = false;
  contractSearchTerm = '';
  contractDropdownOpen = false;
  countrySearchTerm = '';
  countryDropdownOpen = false;
  locationSearchTerm = '';
  locationDropdownOpen = false;
  // markets: readonly Market[] = MARKETS;
  quarterOptions: readonly Quarter[] = QUARTERS;
  clientQualityOptions: readonly ClientQuality[] = CLIENT_QUALITY_OPTIONS;

  form: StandardReviewForm = createDefaultStandardReviewForm();

  // ── Go-Live specific ──────────────────────────────────────────────────────
  get isGoLive(): boolean {
    return this.reviewTypeName.toLowerCase().replace(/[\s\-_]/g, '').includes('golive');
  }

  // ── On-Demand Review specific ─────────────────────────────────────────────
  get isOnDemand(): boolean {
    return this.reviewTypeName.toLowerCase().replace(/[\s\-_]/g, '').includes('ondemand');
  }

  // ── Special Project Review specific ──────────────────────────────────────
  get isSpecialProject(): boolean {
    return this.reviewTypeName.toLowerCase().replace(/[\s\-_]/g, '').includes('specialproject');
  }

  /** True for Go-Live, On-Demand, and Special Project — drives the shared card layout. */
  get useGoLiveLayout(): boolean {
    return this.isGoLive || this.isOnDemand || this.isSpecialProject;
  }

  // On-Demand date fields (both optional)
  onDemandFwStartDate = '';
  onDemandFwEndDate = '';
  onDemandFormErrors: Record<string, string> = {};

  // Special Project — mandatory Type of Project (dates shared with ODR fields)
  specialProjectTypeOfProject = '';
  specialProjectFormErrors: Record<string, string> = {};
  typeOfProjectOptions: string[] = [];
  private typeOfProjectShortCodeMap = new Map<string, string>();
  typeOfProjectDropdownOpen = false;
  typeOfProjectSearchTerm = '';


  glTypeOfReview: 'pre-post' | 'post-only' = 'pre-post';
  glGoLiveDate = '';
  glPreFwStartDate = '';
  glPreFwEndDate = '';
  glInterimCallDate = '';
  glPostFwStartDate = '';
  glPostFwEndDate = '';
  glPublishFinalReport = '';
  glFormErrors: Record<string, string> = {};

  // Go-Live card dropdowns
  glCountryDropdownKey: number | null = null;
  glCountrySearchTerm = '';
  glCityDropdownKey: number | null = null;
  glCitySearchTerm = '';

  // Per-card edit mode: true = editing, false = read-only
  goLiveCardEditMode: boolean[] = [true];

  isGoLiveCardEditing(i: number): boolean { return this.goLiveCardEditMode[i] ?? false; }

  startEditGoLiveCard(i: number): void { this.goLiveCardEditMode[i] = true; }

  saveGoLiveCard(i: number): void {
    this.goLiveCardEditMode[i] = false;
    // Close any open dropdowns for this card
    if (this.glCountryDropdownKey === i) this.glCountryDropdownKey = null;
    if (this.glCityDropdownKey === i) this.glCityDropdownKey = null;
    if (this.managerDropdownKey?.endsWith('-' + i)) this.managerDropdownKey = null;
    if (this.supervisorDropdownKey?.endsWith('-' + i)) this.supervisorDropdownKey = null;
    if (this.reviewerDropdownKey?.endsWith('-' + i)) this.reviewerDropdownKey = null;
  }

  // Go-Live "Add New Location" inline form


  readonly validationRules: Partial<Record<FormFieldKey, ValidationRule>> = {
    ProposedYear: { label: 'Proposed Year', type: 'select' },
    MasterClientName: { label: 'Master Client Name', type: 'select' },
    'D&TProjectName': { label: 'D&T Project Name', type: 'select' },
    ContractNumber: { label: 'Contract Number', type: 'select' },
    Market: { label: 'Market', type: 'select' },
    MarketUnit: { label: 'Market Unit', type: 'select' },
  };



  ngOnInit() {
    this._pickerResizeHandler = () => {
      if (this._activePicker && !this._activePicker.disabled) {
        const picker = this._activePicker;
        picker.blur();
        setTimeout(() => (picker as any).showPicker?.(), 0);
      }
    };
    window.addEventListener('resize', this._pickerResizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', this._pickerResizeHandler!));

    const navReviewType = history.state?.['reviewType'];
    if (navReviewType?.ReviewTypeID) {
      this.reviewTypeId = navReviewType.ReviewTypeID;
      this.reviewTypeName = (navReviewType.ReviewTypeName ?? this.reviewTypeName).replace(/^Go\s+Live Review$/i, 'Go-Live Review');
    }
    if (this.useGoLiveLayout) {
      this.locationCards = [this.createEmptyGlCard(true)];
      this.goLiveCardEditMode = [true];
    }

    this.isLoading = true;
    this.reviewerService.getPurposedYear()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ProposedYearOption[]) => {
          this.proposedYears = response || [];
          this.isLoading = false;
        },
        error: (err) => {
          console.error('PurposedYear error status:', err?.status, 'message:', err?.message, 'full:', err);
          this.isLoading = false;
        }
      });

    this.reviewerService.getAllCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: CountryOption[]) => {
          this.deliveryCountries = response || [];
          // this.prefetchAllCities();
        },
        error: (err) => {
          console.error('AllCountries error status:', err?.status, 'message:', err?.message, 'full:', err);
        }
      });

    this.reviewerService.getProjectDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: ProjectDetail[]) => {
        this.projectDetails = response || [];
        this.masterClientNames = Array.from(new Set(this.projectDetails.map(p => p.ClientName))).sort();
      },
      error: (err) => {
        console.error('Project Details error status:', err?.status, 'message:', err?.message, 'full:', err);
      }
    });

    this.reviewerService.getUsersInformation('Supervisor')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.allUsers = this.mapUsersToOptions(response);
        },
        error: (err) => {
          console.error('GetUsersInformation (Supervisor) error status:', err?.status, 'message:', err?.message, 'full:', err);
        }
      });

    this.masterService.getReviewID()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.existingReviewNames = new Set(
            (res || []).map((item: any) => (item?.ReviewName ?? '').trim().toLowerCase()).filter(Boolean)
          );
        },
        error: (err) => {
          console.error('GetReviewNameList error:', err?.message);
        }
      });

    if (this.isSpecialProject) {
      this.reviewerService.getTypeofProject()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            const list: any[] = Array.isArray(response) ? response : (response?.value ?? response?.data ?? []);
            this.typeOfProjectShortCodeMap.clear();
            this.typeOfProjectOptions = list
              .map((item: any) => {
                const name = (item?.Name ?? '').toString().trim();
                const code = (item?.ShortCode ?? '').toString().trim();
                if (name && code) this.typeOfProjectShortCodeMap.set(name, code);
                return name;
              })
              .filter(Boolean);
          },
          error: (err) => {
            console.error('GetTypeofProject error:', err?.message);
          }
        });
    }
  }

  /** Normalises the GetUsersInformation response into { label, value, role } options for the dropdowns. */
  private mapUsersToOptions(response: any): { label: string; value: string; role: string }[] {
    const list: any[] = Array.isArray(response)
      ? response
      : (response?.value ?? response?.Users ?? response?.data ?? []);
    return list
      .map((u: any) => {
        const enterpriseId = (u?.EID ?? u?.EnterpriseId ?? u?.EnterpriseID ?? '').toString().trim();
        const email = (u?.InternetMail ?? u?.Email ?? u?.EmailId ?? u?.Mail ?? '').toString().trim();
        const value = enterpriseId || email;
        const label = enterpriseId || (u?.DisplayName ?? u?.UserName ?? u?.Name ?? u?.FullName ?? email).toString().trim();
        const role = (u?.Role_Name ?? u?.RoleName ?? '').toString().trim();
        return { label: (label || value).toString().trim(), value: (value || label).toString().trim(), role };
      })
      .filter(o => !!o.value);
  }

  get supervisors(): { label: string; value: string; role: string }[] {
    return this.allUsers;
  }

  get reviewers(): { label: string; value: string; role: string }[] {
    return this.allUsers;
  }

  get managers(): { label: string; value: string; role: string }[] {
    return this.allUsers;
  }

  // ── Supervisor dropdown ──────────────────────────────────────────────────

  toggleSupervisorDropdown(key: string) {
    this.supervisorDropdownKey = this.supervisorDropdownKey === key ? null : key;
    this.supervisorSearchTerm = '';
  }

  get filteredSupervisors(): { label: string; value: string }[] {
    const term = this.supervisorSearchTerm.trim().toLowerCase();
    if (!term) return this.supervisors;
    return this.supervisors.filter(
      (s: { label: string; value: string }) => s.label.toLowerCase().includes(term) || s.value.toLowerCase().includes(term)
    );
  }

  supervisorLabel(value: string): string {
    if (!value) return '';
    return this.supervisors.find((s: { label: string; value: string }) => s.value === value)?.label ?? value;
  }

  selectSupervisor(card: LocationReviewCard, field: 'RCSupervisor' | 'SecondarySupervisor', value: string, cardIndex: number) {
    card[field] = value;
    this.supervisorDropdownKey = null;
    this.supervisorSearchTerm = '';
    if (field === 'RCSupervisor' && value?.trim()) {
      this.locationCardErrors[cardIndex] = { ...this.locationCardErrors[cardIndex], rcSupervisor: undefined };
    }
    this.validateLocationPairs(cardIndex);
  }

  // ── Reviewer dropdown ────────────────────────────────────────────────────

  toggleReviewerDropdown(key: string) {
    this.reviewerDropdownKey = this.reviewerDropdownKey === key ? null : key;
    this.reviewerSearchTerm = '';
  }

  get filteredReviewers(): { label: string; value: string }[] {
    const term = this.reviewerSearchTerm.trim().toLowerCase();
    if (!term) return this.reviewers;
    return this.reviewers.filter(
      (r: { label: string; value: string }) => r.label.toLowerCase().includes(term) || r.value.toLowerCase().includes(term)
    );
  }

  reviewerLabel(value: string): string {
    if (!value) return '';
    return this.reviewers.find((r: { label: string; value: string }) => r.value === value)?.label ?? value;
  }

  selectReviewer(card: LocationReviewCard, field: 'RCReviewer' | 'SecondaryReviewer', value: string, cardIndex: number) {
    card[field] = value;
    this.reviewerDropdownKey = null;
    this.reviewerSearchTerm = '';
    if (field === 'RCReviewer' && value?.trim()) {
      this.locationCardErrors[cardIndex] = { ...this.locationCardErrors[cardIndex], rcReviewer: undefined };
    }
    this.validateLocationPairs(cardIndex);
  }

  // ── Manager dropdown ─────────────────────────────────────────────────────

  toggleManagerDropdown(key: string) {
    this.managerDropdownKey = this.managerDropdownKey === key ? null : key;
    this.managerSearchTerm = '';
  }

  get filteredManagers(): { label: string; value: string }[] {
    const term = this.managerSearchTerm.trim().toLowerCase();
    if (!term) return this.managers;
    return this.managers.filter(
      (m: { label: string; value: string }) => m.label.toLowerCase().includes(term) || m.value.toLowerCase().includes(term)
    );
  }

  managerLabel(value: string): string {
    if (!value) return '';
    return this.managers.find((m: { label: string; value: string }) => m.value === value)?.label ?? value;
  }

  selectManager(card: LocationReviewCard, field: 'RCManager' | 'SecondaryManager', value: string, cardIndex: number) {
    card[field] = value;
    this.managerDropdownKey = null;
    this.managerSearchTerm = '';
    if (field === 'RCManager' && value?.trim()) {
      this.locationCardErrors[cardIndex] = { ...this.locationCardErrors[cardIndex], rcManager: undefined };
    }
    this.validateLocationPairs(cardIndex);
  }

  private prefetchAllCities() {
    this.deliveryCountries.forEach(country => {
      this.reviewerService.getAllCities(country.CountryMasterID)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.citiesByCountryMasterID[country.CountryMasterID] = Array.isArray(response) ? response : [];
          },
          error: (err) => {
            console.error('Prefetch cities error for', country.CountryName, err);
          }
        });
    });
  }


  openGuidelineLink(): void {
    window.open(
      'https://ts.accenture.com/:f:/r/sites/RiskAndCompliance/Rep%20Letter/Shared%20Documents/How%20to%20create%20new%20projects%20in%20Rep%20letter%20tool?csf=1&web=1',
      '_blank'
    );
  }







  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.attachmentErrors = [];
    const validFiles: File[] = [];

    Array.from(input.files).forEach(file => {
      const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '').toLowerCase() : '';
      if (!this.allowedFileExtensions.includes(ext)) {
        this.attachmentErrors.push(`"${file.name}" is not a supported file type.`);
        return;
      }
      if (file.size === 0) {
        this.attachmentErrors.push(`"${file.name}" is empty (0 MB). Please upload a valid file with content.`);
        return;
      }
      if (file.size > this.maxFileSizeBytes) {
        this.attachmentErrors.push(`"${file.name}" exceeds the maximum file size of 20 MB.`);
        return;
      }
      validFiles.push(file);
    });

    const newFiles = validFiles.filter(
      f => !this.attachedFiles.some(existing => existing.name === f.name && existing.size === f.size)
    );
    this.attachedFiles = [...this.attachedFiles, ...newFiles];
    input.value = '';
  }

  removeAttachedFile(index: number) {
    this.attachedFiles.splice(index, 1);
    this.attachmentErrors = [];
  }






  buildLocationCards() {
    const prevCards = this.locationCards;
    const prevErrors = this.locationCardErrors;

    this.locationCards = this.form.LocationsToReview.map((loc, i) => {
      const existing = prevCards.find(card => card.LocationCity === loc);
      if (existing) {
        return { ...existing, IsParentLocation: i === 0 };
      }
      return {
        LocationCity: loc,
        LocationCountry: this.getCountryForCity(loc),
        Quarter: '',
        ProposedMonth: '',
        RCManager: '',
        RCSupervisor: '',
        RCReviewer: '',
        SecondaryManager: '',
        SecondarySupervisor: '',
        SecondaryReviewer: '',
        IsParentLocation: i === 0,
        ParentReviewerName: '',
      };
    });

    // Keep errors in sync — removed cards lose their errors, new cards start clean
    this.locationCardErrors = this.locationCards.map((card) => {
      const oldIdx = prevCards.findIndex(c => c.LocationCity === card.LocationCity);
      return oldIdx >= 0 ? (prevErrors[oldIdx] ?? {}) : {};
    });

    // Preserve per-card edit mode; new cards default to edit mode (true)
    this.goLiveCardEditMode = this.locationCards.map((card) => {
      const oldIdx = prevCards.findIndex(c => c.LocationCity === card.LocationCity);
      return oldIdx >= 0 ? (this.goLiveCardEditMode[oldIdx] ?? true) : true;
    });

    this.updateReviewName();
  }

  validateLocationPairs(cardIndex: number) {
    const card = this.locationCards[cardIndex];
    if (!card) return;
    this.locationCardErrors[cardIndex] = {
      ...this.locationCardErrors[cardIndex],
      manager: (card.RCManager && card.SecondaryManager && card.RCManager === card.SecondaryManager)
        ? 'R&C Manager and Secondary R&C Manager cannot be the same' : undefined,
      supervisor: (card.RCSupervisor && card.SecondarySupervisor && card.RCSupervisor === card.SecondarySupervisor)
        ? 'R&C Supervisor and Secondary R&C Supervisor cannot be the same' : undefined,
      reviewer: (card.RCReviewer && card.SecondaryReviewer && card.RCReviewer === card.SecondaryReviewer)
        ? 'R&C Reviewer and Secondary R&C Reviewer cannot be the same' : undefined,
    };
  }

  private static readonly DUPLICATE_LOCATION_MESSAGE = 'Cannot submit a review with duplicate locations.';

  // Flags every card sharing the same Delivery Country + Location to be Reviewed with another card.
  private refreshDuplicateLocationErrors(): boolean {
    const keyOf = (c: LocationReviewCard) =>
      `${(c.LocationCountry ?? '').trim().toLowerCase()}|${(c.LocationCity ?? '').trim().toLowerCase()}`;
    const counts = new Map<string, number>();
    this.locationCards.forEach(c => {
      if (c.LocationCountry?.trim() && c.LocationCity?.trim()) {
        counts.set(keyOf(c), (counts.get(keyOf(c)) ?? 0) + 1);
      }
    });
    let hasDuplicate = false;
    this.locationCards.forEach((c, i) => {
      const isDup = !!(c.LocationCountry?.trim() && c.LocationCity?.trim()) && (counts.get(keyOf(c)) ?? 0) > 1;
      hasDuplicate = hasDuplicate || isDup;
      this.locationCardErrors[i] = {
        ...this.locationCardErrors[i],
        duplicate: isDup ? StandardReview.DUPLICATE_LOCATION_MESSAGE : undefined,
      };
    });
    return hasDuplicate;
  }

  private validateGoLiveMandatoryFields(): boolean {
    let hasError = false;
    this.locationCards.forEach((card, i) => {
      const errors = { ...this.locationCardErrors[i] };
      errors.country    = card.LocationCountry?.trim() ? undefined : "Select the 'Delivery Country'";
      errors.city       = card.LocationCity?.trim()    ? undefined : "Select the 'Location to be reviewed'";
      errors.rcReviewer   = card.RCReviewer?.trim()   ? undefined : 'R&C Reviewer is mandatory. Please assign a Reviewer before submitting.';
      errors.rcManager    = card.RCManager?.trim()    ? undefined : 'R&C Manager is mandatory. Please assign a Manager before submitting.';
      errors.rcSupervisor = card.RCSupervisor?.trim() ? undefined : 'R&C Supervisor is mandatory. Please assign a Supervisor before submitting.';
      this.locationCardErrors[i] = errors;
      if (errors.country || errors.city || errors.rcReviewer || errors.rcManager || errors.rcSupervisor) {
        hasError = true;
        this.goLiveCardEditMode[i] = true;
      }
    });
    if (this.refreshDuplicateLocationErrors()) {
      hasError = true;
      this.locationCardErrors.forEach((e, i) => { if (e?.duplicate) this.goLiveCardEditMode[i] = true; });
    }
    if (hasError) {
      setTimeout(() => {
        const el = document.querySelector('.field-error');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    return !hasError;
  }

  getMonthsForQuarter(quarter: string): readonly MonthOption[] {
    return QUARTER_MONTH_MAP[quarter as Quarter] ?? [];
  }

  onQuarterChange(card: LocationReviewCard) {
    const allowedMonths = this.getMonthsForQuarter(card.Quarter);
    if (!allowedMonths.includes(card.ProposedMonth as MonthOption)) {
      card.ProposedMonth = '';
    }
  }

  setParentLocation(index: number) {
    this.locationCards.forEach((card, i) => card.IsParentLocation = i === index);
    this.updateReviewName();
  }

  toggleLocationCardReadonly(index: number) {
    this.locationCards[index].isReadonly = !this.locationCards[index].isReadonly;
  }

  private newEmptyCard(isParent = false): LocationReviewCard {
    return {
      LocationCity: '', LocationCountry: '', Quarter: '', ProposedMonth: '',
      RCManager: '', RCSupervisor: '', RCReviewer: '',
      SecondaryManager: '', SecondarySupervisor: '', SecondaryReviewer: '',
      IsParentLocation: isParent, ParentReviewerName: '',
    };
  }

  addNewLocationCard() {
    this.locationCards.push(this.newEmptyCard(false));
    this.locationCardErrors.push({});
    this.cardCities.push([]);
    this.cardCountrySearchTerms.push('');
    this.cardCitySearchTerms.push('');
    this.goLiveCardEditMode.push(true);
  }

  removeLocationCard(index: number) {
    if (this.locationCards.length <= 1) return;
    this.locationCards.splice(index, 1);
    this.locationCardErrors.splice(index, 1);
    this.cardCities.splice(index, 1);
    this.cardCountrySearchTerms.splice(index, 1);
    this.cardCitySearchTerms.splice(index, 1);
    this.refreshDuplicateLocationErrors();
    if (!this.locationCards.some(c => c.IsParentLocation)) {
      this.locationCards[0].IsParentLocation = true;
    }
    this.updateReviewName();
  }

  toggleCardCountryDropdown(index: number) {
    this.cardCountryDropdownOpenIdx = this.cardCountryDropdownOpenIdx === index ? null : index;
    if (this.cardCountryDropdownOpenIdx === index) {
      this.cardCountrySearchTerms[index] = '';
    }
  }

  toggleCardCityDropdown(index: number) {
    this.cardCityDropdownOpenIdx = this.cardCityDropdownOpenIdx === index ? null : index;
    if (this.cardCityDropdownOpenIdx === index) {
      this.cardCitySearchTerms[index] = '';
    }
  }

  getFilteredCardCountries(index: number): CountryOption[] {
    const term = (this.cardCountrySearchTerms[index] ?? '').trim().toLowerCase();
    if (!term) return this.deliveryCountries;
    return this.deliveryCountries.filter(c => c.CountryName.toLowerCase().includes(term));
  }

  getFilteredCardCities(index: number): CityOption[] {
    const cities = this.cardCities[index] ?? [];
    const term = (this.cardCitySearchTerms[index] ?? '').trim().toLowerCase();
    if (!term) return cities;
    return cities.filter(c => c.CityName.toLowerCase().includes(term));
  }

  onCardCountryChange(index: number, countryName: string) {
    const card = this.locationCards[index];
    card.LocationCountry = countryName;
    card.LocationCity = '';
    this.cardCountryDropdownOpenIdx = null;
    this.cardCountrySearchTerms[index] = '';
    this.cardCitySearchTerms[index] = '';
    this.locationCardErrors[index] = { ...this.locationCardErrors[index], country: undefined };
    this.refreshDuplicateLocationErrors();

    const country = this.deliveryCountries.find(c => c.CountryName === countryName);
    if (!country) { this.cardCities[index] = []; return; }

    const cached = this.citiesByCountryMasterID[country.CountryMasterID];
    if (cached) {
      this.cardCities[index] = cached;
    } else {
      this.fetchCitiesForCard(index, country);
    }
    this.updateReviewName();
  }

  onCardCitySelect(index: number, cityName: string) {
    this.locationCards[index].LocationCity = cityName;
    this.cardCityDropdownOpenIdx = null;
    this.cardCitySearchTerms[index] = '';
    this.locationCardErrors[index] = { ...this.locationCardErrors[index], city: undefined };
    this.refreshDuplicateLocationErrors();
    this.updateReviewName();
  }

  private fetchCitiesForCard(cardIndex: number, country: CountryOption) {
    this.isLoading = true;
    this.reviewerService.getAllCities(country.CountryMasterID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.citiesByCountryMasterID[country.CountryMasterID] = Array.isArray(response) ? response : [];
          this.cardCities[cardIndex] = this.citiesByCountryMasterID[country.CountryMasterID];
          this.isLoading = false;
        },
        error: (err) => {
          console.error('AllCities error for card', cardIndex, err);
          this.isLoading = false;
        }
      });
  }

  removeLocation(location: string) {
    this.form.LocationsToReview = this.form.LocationsToReview.filter(l => l !== location);
    this.buildLocationCards();
  }

  private getFinancialYearLabel(year: string): string {
    const trimmed = (year ?? '').trim();
    if (trimmed.length < 2) return '';
    return `FY${trimmed.slice(-2)}`;
  }

  private getParentLocation(): string {
    const parent = this.locationCards.find(card => card.IsParentLocation);
    return parent?.LocationCity ?? '';
  }

  private getCardPrimaryCity(card: LocationReviewCard): string {
    return card.LocationCity ?? '';
  }

  private getCardPrimaryCountry(card: LocationReviewCard): string {
    return card.LocationCountry ?? '';
  }

  private buildReviewName(location: string): string {
    const sprCode = this.typeOfProjectShortCodeMap.get(this.specialProjectTypeOfProject) ?? 'SPR';
    const typeCode = this.isGoLive ? 'GLRR' : this.isOnDemand ? 'ODR' : this.isSpecialProject ? sprCode : this.reviewTypeCode;
    const year    = this.getFinancialYearLabel(this.form.ProposedYear);
    const client  = this.form.MasterClientName?.trim() ?? '';
    const project = this.form['D&TProjectName']?.trim() ?? '';
    const loc     = location?.trim() ?? '';
    const parts = [year, typeCode, client, project, loc]
      .filter((part): part is string => !!part && part.length > 0);
    return parts.join('-');
  }

  updateReviewName() {
    // All review types display with the parent city. For Go-Live, saveGoLiveDynamic() sends
    // buildReviewName('') to the API (without city) so the API can append the card city
    // itself — avoiding double location on submit.
    this.form.ReviewName = this.buildReviewName(this.getParentLocation());
    this.reviewNameDuplicateError = '';
    setTimeout(() => this.adjustReviewNameHeight(), 0);
  }

  onProposedYearChange() {
    this.validateField('ProposedYear');
    this.updateReviewName();
  }

  get filteredClientNames(): string[] {
    const term = this.clientSearchTerm.trim().toLowerCase();
    if (!term) return this.masterClientNames;
    return this.masterClientNames.filter(n => n.toLowerCase().includes(term));
  }

  get filteredProjectNames(): string[] {
    const term = this.projectSearchTerm.trim().toLowerCase();
    if (!term) return this.dtProjectNames;
    return this.dtProjectNames.filter(n => n.toLowerCase().includes(term));
  }

  get filteredContractNumbers(): string[] {
    const term = this.contractSearchTerm.trim().toLowerCase();
    if (!term) return this.contractNumbers;
    return this.contractNumbers.filter(n => n.toLowerCase().includes(term));
  }

  toggleContractDropdown() {
    this.contractDropdownOpen = !this.contractDropdownOpen;
    if (this.contractDropdownOpen) {
      this.contractSearchTerm = '';
      setTimeout(() => this.contractSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectContract(num: string) {
    this.form.ContractNumber = num;
    this.contractDropdownOpen = false;
    this.contractSearchTerm = '';
    this.onContractNumberChange();
  }

  get filteredTypeOfProjectOptions(): string[] {
    const term = this.typeOfProjectSearchTerm.trim().toLowerCase();
    if (!term) return this.typeOfProjectOptions;
    return this.typeOfProjectOptions.filter(n => n.toLowerCase().includes(term));
  }

  get filteredCountries(): CountryOption[] {
    const notSelected = this.deliveryCountries.filter(c => !this.form.DeliveryCountry.includes(c.CountryName));
    const term = this.countrySearchTerm.trim().toLowerCase();
    if (!term) return notSelected;
    return notSelected.filter(c => c.CountryName.toLowerCase().includes(term));
  }

  get filteredCities(): CityOption[] {
    const notSelected = this.availableCities.filter(c => !this.form.LocationsToReview.includes(c.CityName));
    const term = this.locationSearchTerm.trim().toLowerCase();
    if (!term) return notSelected;
    return notSelected.filter(c => c.CityName.toLowerCase().includes(term));
  }

  @ViewChild('clientSearchInput') clientSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('projectSearchInput') projectSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('contractSearchInput') contractSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('countrySearchInput') countrySearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('locationSearchInput') locationSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('reviewNameTextarea') reviewNameTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('typeOfProjectSearchInput') typeOfProjectSearchInput?: ElementRef<HTMLInputElement>;

  private adjustReviewNameHeight() {
    const el = this.reviewNameTextarea?.nativeElement;
    if (!el) return;
    // Sync value directly so scrollHeight is accurate even before Angular change detection runs.
    if (el.value !== this.form.ReviewName) {
      el.value = this.form.ReviewName;
    }
    el.rows = 1;
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const contentHeight = el.scrollHeight - padding;
    el.rows = Math.max(1, Math.round(contentHeight / lineHeight));
  }

  toggleClientDropdown() {
    this.clientDropdownOpen = !this.clientDropdownOpen;
    if (this.clientDropdownOpen) {
      this.clientSearchTerm = '';
      this.projectDropdownOpen = false;
      setTimeout(() => this.clientSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectClient(name: string) {
    this.form.MasterClientName = name;
    this.clientDropdownOpen = false;
    this.clientSearchTerm = '';
    this.onMasterClientNameChange();
  }

  toggleProjectDropdown() {
    this.projectDropdownOpen = !this.projectDropdownOpen;
    if (this.projectDropdownOpen) {
      this.projectSearchTerm = '';
      this.clientDropdownOpen = false;
      setTimeout(() => this.projectSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectProject(name: string) {
    this.form['D&TProjectName'] = name;
    this.projectDropdownOpen = false;
    this.projectSearchTerm = '';
    this.onDtProjectNameChange();
  }

  toggleTypeOfProjectDropdown() {
    this.typeOfProjectDropdownOpen = !this.typeOfProjectDropdownOpen;
    if (this.typeOfProjectDropdownOpen) {
      this.typeOfProjectSearchTerm = '';
      setTimeout(() => this.typeOfProjectSearchInput?.nativeElement.focus(), 0);
    }
  }

  selectTypeOfProject(name: string) {
    this.specialProjectTypeOfProject = name;
    this.typeOfProjectDropdownOpen = false;
    this.typeOfProjectSearchTerm = '';
    this.onSpecialProjectTypeOfProjectChange();
  }

  toggleCountryDropdown() {
    this.countryDropdownOpen = !this.countryDropdownOpen;
    if (this.countryDropdownOpen) {
      this.countrySearchTerm = '';
      this.clientDropdownOpen = false;
      this.projectDropdownOpen = false;
      this.locationDropdownOpen = false;
      setTimeout(() => this.countrySearchInput?.nativeElement.focus(), 0);
    }
  }

  addCountryFromDropdown(name: string) {
    if (name && !this.form.DeliveryCountry.includes(name)) {
      this.form.DeliveryCountry.push(name);
      this.refreshAvailableCities();
      this.validateField('DeliveryCountry');
    }
    this.countrySearchTerm = '';
    this.countryDropdownOpen = false;
  }

  removeCountryWithValidation(name: string) {
    this.form.DeliveryCountry = this.form.DeliveryCountry.filter(c => c !== name);
    this.refreshAvailableCities();
    this.validateField('DeliveryCountry');
    this.validateField('LocationsToReview');
  }

  toggleLocationDropdown() {
    this.locationDropdownOpen = !this.locationDropdownOpen;
    if (this.locationDropdownOpen) {
      this.locationSearchTerm = '';
      this.clientDropdownOpen = false;
      this.projectDropdownOpen = false;
      this.countryDropdownOpen = false;
      setTimeout(() => this.locationSearchInput?.nativeElement.focus(), 0);
    }
  }

  addLocationFromDropdown(city: string) {
    if (city && !this.form.LocationsToReview.includes(city)) {
      this.form.LocationsToReview.push(city);
      this.buildLocationCards();
      this.validateField('LocationsToReview');
    }
    this.locationSearchTerm = '';
    this.locationDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.clientDropdownOpen && !target.closest('.client-select')) {
      this.clientDropdownOpen = false;
    }
    if (this.projectDropdownOpen && !target.closest('.project-select')) {
      this.projectDropdownOpen = false;
    }
    if (this.typeOfProjectDropdownOpen && !target.closest('.type-of-project-select')) {
      this.typeOfProjectDropdownOpen = false;
    }
    if (this.countryDropdownOpen && !target.closest('.country-select')) {
      this.countryDropdownOpen = false;
    }
    if (this.locationDropdownOpen && !target.closest('.location-select')) {
      this.locationDropdownOpen = false;
    }
    if (this.contractDropdownOpen && !target.closest('.contract-select')) {
      this.contractDropdownOpen = false;
    }
    if (this.supervisorDropdownKey && !target.closest('.supervisor-select')) {
      this.supervisorDropdownKey = null;
    }
    if (this.reviewerDropdownKey && !target.closest('.reviewer-select')) {
      this.reviewerDropdownKey = null;
    }
    if (this.managerDropdownKey && !target.closest('.manager-select')) {
      this.managerDropdownKey = null;
    }
    if (this.cardCountryDropdownOpenIdx !== null && !target.closest('.card-country-select')) {
      this.cardCountryDropdownOpenIdx = null;
    }
    if (this.cardCityDropdownOpenIdx !== null && !target.closest('.card-city-select')) {
      this.cardCityDropdownOpenIdx = null;
    }
    if (this.glCountryDropdownKey !== null && !target.closest(`.gl-country-select-${this.glCountryDropdownKey}`)) {
      this.glCountryDropdownKey = null;
    }
  }

  // Close the Delivery Country dropdown when focus moves (e.g. Tab) to another field
  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent) {
    const target = event.target as HTMLElement;
    if (this.cardCountryDropdownOpenIdx !== null && !target.closest('.card-country-select')) {
      this.cardCountryDropdownOpenIdx = null;
    }
    if (this.glCountryDropdownKey !== null && !target.closest(`.gl-country-select-${this.glCountryDropdownKey}`)) {
      this.glCountryDropdownKey = null;
    }
  }

  onMasterClientNameChange() {
    this.validateField('MasterClientName');
    this.dtProjectNames = Array.from(new Set(
      this.projectDetails
        .filter(p => p.ClientName === this.form.MasterClientName)
        .map(p => p.RepLetterProjectNm)
    ));
    this.form['D&TProjectName'] = '';
    this.contractNumbers = [];
    this.form.ContractNumber = '';
    this.form.Market = '';
    this.form.MarketUnit = '';
    this.updateReviewName();
  }

  onDtProjectNameChange() {
    this.validateField('D&TProjectName');
    this.contractNumbers = Array.from(new Set(this.projectDetails
      .filter(p => p.RepLetterProjectNm === this.form['D&TProjectName'] && p.ClientName === this.form.MasterClientName)
      .map(p => p.ContractNumber)));
    this.form.ContractNumber = '';
    this.contractDropdownOpen = false;
    this.contractSearchTerm = '';
    this.form.Market = '';
    this.form.MarketUnit = '';
    this.updateReviewName();
  }

  onContractNumberChange() {
    this.validateField('ContractNumber');
    const match = this.projectDetails.find(
      p => p.RepLetterProjectNm === this.form['D&TProjectName'] && p.ContractNumber === this.form.ContractNumber
    );
    this.form.Market = match?.Market ?? '';
    this.form.MarketUnit = match?.MarketUnit ?? '';
    this.validateField('Market');
    this.validateField('MarketUnit');
  }

  private createEmptyGlCard(isParent = false): LocationReviewCard {
    return {
      LocationCity: '',
      LocationCountry: '',
      Quarter: '',
      ProposedMonth: '',
      RCManager: '',
      RCSupervisor: '',
      RCReviewer: '',
      SecondaryManager: '',
      SecondarySupervisor: '',
      SecondaryReviewer: '',
      IsParentLocation: isParent,
      ParentReviewerName: '',
    };
  }

  addNewGoLiveCard() {
    this.locationCards.push(this.createEmptyGlCard(this.locationCards.length === 0));
    this.goLiveCardEditMode.push(true);
    this.recalcPublishDate();
  }

  removeGoLiveCard(index: number) {
    this.locationCards.splice(index, 1);
    this.goLiveCardEditMode.splice(index, 1);
    this.locationCardErrors.splice(index, 1);
    this.refreshDuplicateLocationErrors();
    if (this.locationCards.length > 0 && !this.locationCards.some(c => c.IsParentLocation)) {
      this.locationCards[0].IsParentLocation = true;
    }
    this.syncGlCardsToForm();
  }


  private getGlCardCities(card: LocationReviewCard): CityOption[] {
    const countryName = card.LocationCountry ?? '';
    if (!countryName) return [];
    const country = this.deliveryCountries.find(c => c.CountryName === countryName);
    if (!country) return [];
    return this.citiesByCountryMasterID[country.CountryMasterID] ?? [];
  }

  filteredGlCardCountries(): CountryOption[] {
    const term = this.glCountrySearchTerm.trim().toLowerCase();
    if (!term) return this.deliveryCountries;
    return this.deliveryCountries.filter(c => c.CountryName.toLowerCase().includes(term));
  }

  filteredGlCities(card: LocationReviewCard): CityOption[] {
    const cities = this.getGlCardCities(card);
    const term = this.glCitySearchTerm.trim().toLowerCase();
    if (!term) return cities;
    return cities.filter(c => c.CityName.toLowerCase().includes(term));
  }

  toggleGlCardCountryDropdown(i: number) {
    if (this.glCountryDropdownKey === i) {
      this.glCountryDropdownKey = null;
    } else {
      this.glCountryDropdownKey = i;
      this.glCityDropdownKey = null;
      this.glCountrySearchTerm = '';
    }
  }

  addGlCardCountry(card: LocationReviewCard, name: string) {
    card.LocationCountry = name;
    card.LocationCity = '';
    const country = this.deliveryCountries.find(c => c.CountryName === name);
    if (country && !this.citiesByCountryMasterID[country.CountryMasterID]) {
      this.isLoading = true;
      this.reviewerService.getAllCities(country.CountryMasterID)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.citiesByCountryMasterID[country.CountryMasterID] = Array.isArray(response) ? response : [];
            this.isLoading = false;
          },
          error: () => { this.isLoading = false; }
        });
    }
    this.glCountrySearchTerm = '';
    this.glCountryDropdownKey = null;
    const cardIndex = this.locationCards.indexOf(card);
    if (cardIndex >= 0) {
      this.locationCardErrors[cardIndex] = { ...this.locationCardErrors[cardIndex], country: undefined };
    }
    this.refreshDuplicateLocationErrors();
    this.syncGlCardsToForm();
  }

  toggleGlCityDropdown(i: number) {
    if (this.glCityDropdownKey === i) {
      this.glCityDropdownKey = null;
    } else {
      this.glCityDropdownKey = i;
      this.glCountryDropdownKey = null;
      this.glCitySearchTerm = '';
    }
  }

  selectGlCity(card: LocationReviewCard, cityName: string) {
    card.LocationCity = cityName;
    this.glCitySearchTerm = '';
    this.glCityDropdownKey = null;
    const cardIndex = this.locationCards.indexOf(card);
    if (cardIndex >= 0) {
      this.locationCardErrors[cardIndex] = { ...this.locationCardErrors[cardIndex], city: undefined };
    }
    this.refreshDuplicateLocationErrors();
    this.syncGlCardsToForm();
  }

  private syncGlCardsToForm() {
    this.form.LocationsToReview = this.locationCards.map(c => c.LocationCity).filter(Boolean);
    this.form.DeliveryCountry = Array.from(
      new Set(this.locationCards.map(c => c.LocationCountry).filter(Boolean))
    );
    this.updateReviewName();
    this.recalcPublishDate();
  }

  get marketOptions(): string[] {
    return Array.from(new Set(this.projectDetails.map(p => p.Market).filter(Boolean))).sort();
  }

  get marketUnitOptions(): string[] {
    const src = this.form.Market
      ? this.projectDetails.filter(p => p.Market === this.form.Market)
      : this.projectDetails;
    return Array.from(new Set(src.map(p => p.MarketUnit).filter(Boolean))).sort();
  }

  onMarketChange() {
    this.form.MarketUnit = '';
    this.validateField('Market');
  }

  removeLocationWithValidation(location: string) {
    this.removeLocation(location);
    this.validateField('LocationsToReview');
  }

  // Rebuilds the city list as the union of cities across all selected countries,
  // then prunes any selected locations whose country is no longer selected.
  private refreshAvailableCities() {
    const cities: CityOption[] = [];
    const seen = new Set<string>();

    for (const countryName of this.form.DeliveryCountry) {
      const country = this.deliveryCountries.find(c => c.CountryName === countryName);
      if (!country) continue;

      const cached = this.citiesByCountryMasterID[country.CountryMasterID];
      if (!cached) {
        this.fetchCitiesForCountry(country);
        continue;
      }

      for (const city of cached) {
        if (!seen.has(city.CityName)) {
          seen.add(city.CityName);
          cities.push(city);
        }
      }
    }

    this.availableCities = cities;

    const availableNames = new Set(cities.map(c => c.CityName));
    this.form.LocationsToReview = this.form.LocationsToReview.filter(loc => availableNames.has(loc));
    this.buildLocationCards();
  }

  private fetchCitiesForCountry(country: CountryOption) {
    this.isLoading = true;
    this.reviewerService.getAllCities(country.CountryMasterID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.citiesByCountryMasterID[country.CountryMasterID] = Array.isArray(response) ? response : [];
          this.refreshAvailableCities();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('AllCities error status:', err?.status, 'message:', err?.message, 'full:', err);
          this.isLoading = false;
        }
      });
  }

  private getCountryForCity(cityName: string): string {
    const city = this.availableCities.find(c => c.CityName === cityName);
    if (!city) return '';
    const country = this.deliveryCountries.find(c => c.CountryMasterID === city.CountryMasterID);
    return country?.CountryName ?? '';
  }

  // addLocation(event: Event) {
  //   const select = event.target as HTMLSelectElement;
  //   const city = select.value;
  //   if (city && !this.form.LocationsToReview.includes(city)) {
  //     this.form.LocationsToReview.push(city);
  //     this.buildLocationCards();
  //   }
  //   select.value = '';
  // }

  // addLocationWithValidation(event: Event) {
  //   this.addLocation(event);
  //   this.validateField('LocationsToReview');
  // }

  onMasterClientIDKeyPress(event: KeyboardEvent) {
    if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault();
      this.formErrors['MasterClientID'] = 'Only numbers are allowed';
    }
  }

  onMasterClientIDInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const original = input.value;
    const numericOnly = original.replace(/\D/g, '');
    input.value = numericOnly;
    this.form.MasterClientID = numericOnly;
    if (/\D/.test(original)) {
      this.formErrors['MasterClientID'] = 'Only numbers are allowed';
    } else {
      delete this.formErrors['MasterClientID'];
    }
  }

  onAdditionalContractNumberKeyPress(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if (event.key.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault();
      this.formErrors['AdditionalContractNumber'] = this.getAdditionalContractNumberError(input.value);
    } else if (input.value.length >= 10) {
      event.preventDefault();
    }
  }

  onAdditionalContractNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const original = input.value;
    const numericOnly = original.replace(/\D/g, '').slice(0, 10);
    input.value = numericOnly;
    this.form.AdditionalContractNumber = numericOnly;
    if (/\D/.test(original) || !this.isValidAdditionalContractNumber(numericOnly)) {
      this.formErrors['AdditionalContractNumber'] = this.getAdditionalContractNumberError(numericOnly);
    } else {
      delete this.formErrors['AdditionalContractNumber'];
    }
  }

  // Already starts with 9 -> only the length is wrong; otherwise show the full rule.
  getAdditionalContractNumberError(value: string | null | undefined): string {
    return String(value ?? '').trim().startsWith('9')
      ? 'Additional Contract Number must be a 10-digit number'
      : 'Additional Contract Number must be a 10-digit number starting with 9';
  }

  // Optional field: empty is valid; otherwise exactly 10 digits starting with 9.
  isValidAdditionalContractNumber(value: string | null | undefined): boolean {
    const v = String(value ?? '').trim();
    return !v || /^9\d{9}$/.test(v);
  }

  draftSavedMessage = false;

  formErrors: Record<string, string> = {};

  get isFormDirty(): boolean {
    return !!(
      this.form.ProposedYear ||
      this.form.MasterClientName ||
      this.form['D&TProjectName'] ||
      this.form.ContractNumber ||
      this.form.AdditionalContractNumber ||
      this.form.CustomerRelationshipName ||
      this.form.PlanName ||
      this.form.QADRecommendation ||
      this.form.ClientQualityCQI ||
      this.form.ClientAccountLead ||
      this.form.OperationAccountLead ||
      this.form.MasterClientID ||
      this.form.Comments ||
      this.locationCards.some(c => c.LocationCountry || c.LocationCity || c.Quarter || c.RCManager || c.RCSupervisor || c.RCReviewer || c.SecondaryManager || c.SecondarySupervisor || c.SecondaryReviewer) ||
      this.form.DeliveryCountry.length > 0 ||
      this.form.LocationsToReview.length > 0 ||
      this.attachedFiles.length > 0 ||
      (this.isGoLive && !!(this.glGoLiveDate || this.glPostFwEndDate)) ||
      (this.isOnDemand && !!(this.onDemandFwStartDate || this.onDemandFwEndDate)) ||
      (this.isSpecialProject && !!(this.onDemandFwStartDate || this.onDemandFwEndDate || this.specialProjectTypeOfProject))
    );
  }

  validateForm(): boolean {
    this.formErrors = {};

    Object.keys(this.validationRules).forEach((field) => {
      this.validateField(field as FormFieldKey);
    });

    if (!this.isValidAdditionalContractNumber(this.form.AdditionalContractNumber)) {
      this.formErrors['AdditionalContractNumber'] = this.getAdditionalContractNumberError(this.form.AdditionalContractNumber);
    }

    return Object.keys(this.formErrors).length === 0;
  }

  validateField(fieldName: FormFieldKey) {
    const rule = this.validationRules[fieldName];
    if (!rule) {
      return;
    }

    const value = this.form[fieldName];
    const isEmpty = Array.isArray(value) ? value.length === 0 : !String(value ?? '').trim();

    if (isEmpty) {
      const action = rule.type === 'select' ? 'Select' : 'Fill';
      this.formErrors[fieldName] = `${action} the '${rule.label}'`;
      return;
    }

    delete this.formErrors[fieldName];
  }

  scrollToFirstError() {
    const errorFields = Object.keys(this.formErrors);
    if (errorFields.length === 0) return;

    const firstErrorField = errorFields[0];
    const element = document.querySelector(`[data-field="${firstErrorField}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private validateGoLiveFields(): boolean {
    this.glFormErrors = {};
    if (!this.glGoLiveDate) {
      this.glFormErrors['GoLiveDate'] = "Select the 'Go-Live Date'";
    }
    if (this.glPreFwEndDate) {
      const preBeforeStart = !!(this.glPreFwStartDate && this.glPreFwEndDate < this.glPreFwStartDate);
      if (preBeforeStart) {
        this.glFormErrors['PreFwEndDate'] = 'Pre Go-Live Fieldwork End Date cannot be earlier than Pre Go-Live Fieldwork Start Date.';
      }
    }
    if (!this.glPostFwEndDate) {
      this.glFormErrors['PostFwEndDate'] = "Select the 'Post Go-Live Fieldwork End Date'";
    } else {
      const postBeforeStart = !!(this.glPostFwStartDate && this.glPostFwEndDate < this.glPostFwStartDate);
      if (postBeforeStart) {
        this.glFormErrors['PostFwEndDate'] = 'Post Go-Live Fieldwork End Date cannot be earlier than Post Go-Live Fieldwork Start Date.';
      }
    }
    if (Object.keys(this.glFormErrors).length > 0) {
      setTimeout(() => {
        const el = document.querySelector('.gl-field-error');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return false;
    }
    return true;
  }

  private adjustToNextWorkingDay(dateStr: string): string {
    if (!dateStr) return dateStr;
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return dateStr;
    const dow = d.getDay();
    if (dow === 6) d.setDate(d.getDate() + 2);
    else if (dow === 0) d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private addWorkingDays(dateStr: string, days: number): string {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    let remaining = days;
    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) remaining--;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private subtractWorkingDays(dateStr: string, days: number): string {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    let remaining = days;
    while (remaining > 0) {
      d.setDate(d.getDate() - 1);
      const dow = d.getDay(); // 0 = Sun, 6 = Sat
      if (dow !== 0 && dow !== 6) remaining--;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  openDatePicker(input: HTMLInputElement): void {
    this._activePicker = input;
    (input as any).showPicker?.();
  }

  formatDateDisplay(dateStr: string): string {
    return this.dateFormatSvc.format(dateStr);
  }

  onGoLiveDateChange(): void {
    if (this.glGoLiveDate) {
      this.glGoLiveDate = this.adjustToNextWorkingDay(this.glGoLiveDate);

      delete this.glFormErrors['GoLiveDate'];
      if (this.glTypeOfReview === 'pre-post') {
        this.glPreFwStartDate = this.subtractWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.PRE_FW_START_FROM_GO_LIVE);
        this.glPreFwEndDate = this.subtractWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.PRE_FW_END_FROM_GO_LIVE);
        this.glInterimCallDate = this.subtractWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.INTERIM_CALL_FROM_GO_LIVE);
        delete this.glFormErrors['PreFwStartDate'];
        this.setPreFwEndDateError();
      }
      this.glPostFwStartDate = this.addWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.POST_FW_START_FROM_GO_LIVE);
      delete this.glFormErrors['PostFwStartDate'];
    } else {
      delete this.glFormErrors['GoLiveDate'];
      this.glPreFwStartDate = '';
      this.glPreFwEndDate = '';
      this.glInterimCallDate = '';
      this.glPostFwStartDate = '';
    }
    this.recalcPublishDate();
  }

  onTypeOfReviewChange(): void {
    if (this.glTypeOfReview === 'post-only') {
      this.glPreFwStartDate = '';
      this.glPreFwEndDate = '';
      this.glInterimCallDate = '';
    } else if (this.glGoLiveDate) {
      this.glPreFwStartDate = this.subtractWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.PRE_FW_START_FROM_GO_LIVE);
      this.glPreFwEndDate = this.subtractWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.PRE_FW_END_FROM_GO_LIVE);
      this.glInterimCallDate = this.subtractWorkingDays(this.glGoLiveDate, GLRR_FIELD_CONFIG.INTERIM_CALL_FROM_GO_LIVE);
    }
  }

  onPreFwStartDateChange(): void {
    delete this.glFormErrors['PreFwStartDate'];
    if (this.glPreFwEndDate) {
      this.setPreFwEndDateError();
    } else {
      delete this.glFormErrors['PreFwEndDate'];
    }
  }

  onPreFwEndDateChange(): void {
    if (this.glPreFwEndDate) {
      this.setPreFwEndDateError();
    } else {
      delete this.glFormErrors['PreFwEndDate'];
    }
  }

  onPostFwStartDateChange(): void {
    delete this.glFormErrors['PostFwStartDate'];
    if (this.glPostFwEndDate) {
      this.setPostFwEndDateError();
    } else {
      delete this.glFormErrors['PostFwEndDate'];
    }
    this.recalcPublishDate();
  }

  onPostFwEndDateChange(): void {
    if (this.glPostFwEndDate) {
      this.setPostFwEndDateError();
    } else {
      delete this.glFormErrors['PostFwEndDate'];
    }
    this.recalcPublishDate();
  }

  private setPreFwEndDateError(): void {
    const beforeStart = !!(this.glPreFwStartDate && this.glPreFwEndDate < this.glPreFwStartDate);
    if (beforeStart) {
      this.glFormErrors['PreFwEndDate'] = 'Pre Go-Live Fieldwork End Date cannot be earlier than Pre Go-Live Fieldwork Start Date.';
    } else {
      delete this.glFormErrors['PreFwEndDate'];
    }
  }

  private setPostFwEndDateError(): void {
    const beforeStart = !!(this.glPostFwStartDate && this.glPostFwEndDate < this.glPostFwStartDate);
    if (beforeStart) {
      this.glFormErrors['PostFwEndDate'] = 'Post Go-Live Fieldwork End Date cannot be earlier than Post Go-Live Fieldwork Start Date.';
    } else {
      delete this.glFormErrors['PostFwEndDate'];
    }
  }

  // ── On-Demand date handlers ───────────────────────────────────────────────

  onDemandFwStartDateChange(): void {
    delete this.onDemandFormErrors['FwStartDate'];
    // Re-evaluate end-date order error when start changes
    if (this.onDemandFwEndDate && this.onDemandFwStartDate && this.onDemandFwEndDate < this.onDemandFwStartDate) {
      this.onDemandFormErrors['FwEndDate'] = 'Fieldwork End Date cannot be earlier than the Start Date.';
    } else if (this.onDemandFormErrors['FwEndDate']?.includes('earlier')) {
      delete this.onDemandFormErrors['FwEndDate'];
    }
  }

  onDemandFwEndDateChange(): void {
    if (this.onDemandFwEndDate) {
      if (this.onDemandFwStartDate && this.onDemandFwEndDate < this.onDemandFwStartDate) {
        this.onDemandFormErrors['FwEndDate'] = 'Fieldwork End Date cannot be earlier than the Start Date.';
      } else {
        delete this.onDemandFormErrors['FwEndDate'];
      }
    } else {
      delete this.onDemandFormErrors['FwEndDate'];
    }
  }

  onSpecialProjectTypeOfProjectChange(): void {
    if (this.specialProjectTypeOfProject.trim()) {
      delete this.specialProjectFormErrors['TypeOfProject'];
    }
    this.updateReviewName();
  }

  private validateSpecialProjectFields(): boolean {
    if (!this.specialProjectTypeOfProject.trim()) {
      this.specialProjectFormErrors['TypeOfProject'] = "Select the 'Type of Project'";
      return false;
    }
    delete this.specialProjectFormErrors['TypeOfProject'];
    return true;
  }

  private recalcPublishDate(): void {
    if (!this.glPostFwEndDate || this.glFormErrors['PostFwEndDate']) {
      this.glPublishFinalReport = '';
      return;
    }
    const days = this.locationCards.length > 1 ? GLRR_FIELD_CONFIG.TARGET_PUB_WORKING_DAYS_MULTI : GLRR_FIELD_CONFIG.TARGET_PUB_WORKING_DAYS_SINGLE;
    this.glPublishFinalReport = this.addWorkingDays(this.glPostFwEndDate, days);
  }

  private getLocalDateTime(): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T` +
      `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  }

  onClientAccountLeadChange(val: string): void {
    this.clientAccountLeadSelected = false;
    this.clientAccountLeadError = val?.trim() ? 'Entered EID is not valid. Please enter a valid EID.' : '';
  }

  onClientAccountLeadPersonSelected(): void {
    this.clientAccountLeadSelected = true;
    this.clientAccountLeadError = '';
  }

  onOperationAccountLeadChange(val: string): void {
    this.operationAccountLeadSelected = false;
    this.operationAccountLeadError = val?.trim() ? 'Entered EID is not valid. Please enter a valid EID.' : '';
  }

  onOperationAccountLeadPersonSelected(): void {
    this.operationAccountLeadSelected = true;
    this.operationAccountLeadError = '';
  }

  saveAsDraft() {
    this.saveAndSubmitPayload('Draft');
  }

  submitForm() {
    // Evaluate all validations first so their error messages render together —
    // do not short-circuit before the location card validation runs.
    const formValid = this.validateForm();
    const goLiveDatesValid = this.isGoLive ? this.validateGoLiveFields() : true;
    const specialProjectValid = this.isSpecialProject ? this.validateSpecialProjectFields() : true;
    // The location card always renders the Go-Live layout with required fields,
    // so its mandatory-field validation must always run (not gated on isGoLive).
    const locationCardsValid = this.validateGoLiveMandatoryFields();

    if (!formValid) { this.scrollToFirstError(); return; }
    if (!goLiveDatesValid) return;
    if (!specialProjectValid) return;
    if (!locationCardsValid) return;
    if (this.clientAccountLeadError || this.operationAccountLeadError) return;
    if (this.hasLocationPairErrorsWithScroll()) return;
    if (this.hasDuplicateReviewNameWithScroll()) return;
    if (this.hasAttachmentErrorsWithScroll()) return;
    this.saveAndSubmitPayload('Planned');
  }

  private hasDuplicateReviewNameWithScroll(): boolean {
    const cardNames = this.locationCards.length > 0
      ? this.locationCards.map(card => this.buildReviewName(this.getCardPrimaryCity(card)))
      : [this.buildReviewName('')];
    const duplicate = cardNames.some(name => name && this.existingReviewNames.has(name.trim().toLowerCase()));
    if (duplicate) {
      this.reviewNameDuplicateError = 'A review with this name already exists. Please update the review details to generate a unique Review Name.';
      setTimeout(() => {
        const el = document.querySelector('[data-field="ReviewName"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    return duplicate;
  }

  private hasAttachmentErrorsWithScroll(): boolean {
    if (this.attachmentErrors.length === 0) return false;
    setTimeout(() => {
      const el = document.querySelector('.attachment-error');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    return true;
  }

  private hasLocationPairErrorsWithScroll(): boolean {
    const hasErrors = this.locationCardErrors.some(
      e => e && (e.manager || e.supervisor || e.reviewer)
    );
    if (hasErrors) {
      setTimeout(() => {
        const el = document.querySelector('.field-error');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
    return hasErrors;
  }

  private async saveAndSubmitPayload(currentStatus: string): Promise<void> {
    this.isLoading = true;
    this.CreatedBy = this.rebarAuth.getUserName() ?? '';
    this.CreatedDate = this.getLocalDateTime();

    if (this.isGoLive) {
      await this.saveGoLiveDynamic(currentStatus);
      return;
    }

    if (this.isOnDemand || this.isSpecialProject) {
      await this.saveVariantReviewDynamic(currentStatus);
      return;
    }

    const folder = (this.form.ReviewName ?? '').trim();
    const reviewNameBase = this.buildReviewName('');

    // ── Documents (structure unchanged) ──────────────────────────────
    const documents: ReviewDocument[] = await Promise.all(
      this.attachedFiles.map(async (file, index) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          DocSeq: index + 1,
          DocumentType: 'Standard Review Attachment',
          FileName: file.name,
          StorageFilePath: folder ? `${folder}/${file.name}` : file.name,
          FileSize: file.size,
          FileType: ext,
          Description: '',
          fileContent,
          UploadedBy: this.CreatedBy,
        };
      })
    );

    // Helper — push only if value is non-empty
    const pushField = (arr: any[], fieldName: string, fieldValue: string | number | null | undefined, dataType: string) => {
      const val = (fieldValue ?? '').toString().trim();
      if (val !== '') arr.push({ FieldName: fieldName, FieldValue: val, DataType: dataType });
    };

    // ── CommonFields ─────────────────────────────────────────────────
    const CommonFields: any[] = [];

    CommonFields.push({ FieldName: 'ReviewTypeID', FieldValue: '1', DataType: 'int' });

    const yearObj = this.proposedYears.find(y => y.ProposedYear.toString() === this.form.ProposedYear.toString());
    if (yearObj) {
      CommonFields.push({ FieldName: 'ProposedYearId', FieldValue: yearObj.ProposedYearId.toString(), DataType: 'int' });
    }

    pushField(CommonFields, 'ClientName',                 this.form.MasterClientName,           'nvarchar');
    pushField(CommonFields, 'ProjectName',                this.form['D&TProjectName'],           'nvarchar');
    pushField(CommonFields, 'ContractNumber',             this.form.ContractNumber,              'nvarchar');
    pushField(CommonFields, 'AdditionalContractNumber',   this.form.AdditionalContractNumber,    'nvarchar');
    pushField(CommonFields, 'Market',                     this.form.Market,                      'nvarchar');
    pushField(CommonFields, 'MarketUnit',                 this.form.MarketUnit,                  'nvarchar');
    pushField(CommonFields, 'CustomerRelationshipName',   this.form.CustomerRelationshipName,    'nvarchar');
    pushField(CommonFields, 'PlanName',                   this.form.PlanName,                    'nvarchar');
    pushField(CommonFields, 'QADRecommendation',          this.form.QADRecommendation,           'nvarchar');
    pushField(CommonFields, 'ClientQualityCQI',           this.form.ClientQualityCQI,            'nvarchar');
    pushField(CommonFields, 'ClientAccountLead',          this.form.ClientAccountLead,           'nvarchar');
    pushField(CommonFields, 'OperationAccountLead',       this.form.OperationAccountLead,        'nvarchar');
    pushField(CommonFields, 'MasterClientID',             this.form.MasterClientID,              'nvarchar');
    pushField(CommonFields, 'Comments',                   this.form.Comments,                    'nvarchar');

    const nv = (v: string | undefined | null): string => (v ?? '').trim();
    const singleOrMulti = this.locationCards.length === 1 ? 'Single Location' : 'Multiple Location';
    CommonFields.push({ FieldName: 'SingleMultipleLocation', FieldValue: singleOrMulti, DataType: 'nvarchar' });

    // ── LocationFields ────────────────────────────────────────────────
    const LocationFields: any[] = [];

    this.locationCards.forEach(card => {
      const locationName = nv(card.LocationCity);
      const isParent = card.IsParentLocation ? 1 : 0;
      // Always include the primary card (even with no city/country) so the
      // SP JOIN returns a row for this review. Skip secondary cards with no data.
      if (!locationName && !nv(card.LocationCountry) && !card.IsParentLocation) return;
      [
        { FieldName: 'DeliveryCountry',      FieldValue: nv(card.LocationCountry),     DataType: 'nvarchar' },
        { FieldName: 'LocationToBeReviewed', FieldValue: locationName,                 DataType: 'nvarchar' },
        { FieldName: 'Quarter',              FieldValue: nv(card.Quarter),             DataType: 'nvarchar' },
        { FieldName: 'ProposedMonth',        FieldValue: nv(card.ProposedMonth),       DataType: 'nvarchar' },
        { FieldName: 'RCManager',            FieldValue: nv(card.RCManager),           DataType: 'nvarchar' },
        { FieldName: 'RCSupervisor',         FieldValue: nv(card.RCSupervisor),        DataType: 'nvarchar' },
        { FieldName: 'RCReviewer',           FieldValue: nv(card.RCReviewer),          DataType: 'nvarchar' },
        { FieldName: 'SecondaryRCManager',    FieldValue: nv(card.SecondaryManager),    DataType: 'nvarchar' },
        { FieldName: 'SecondaryRCSupervisor', FieldValue: nv(card.SecondarySupervisor), DataType: 'nvarchar' },
        { FieldName: 'SecondaryRCReviewer',   FieldValue: nv(card.SecondaryReviewer),   DataType: 'nvarchar' },
      ].forEach(f => LocationFields.push({ LocationName: locationName, IsParentLocation: isParent, ...f }));
    });

    // ── Assemble final payload ────────────────────────────────────────
    const payload: any = {
      category:      'SaveReviewDynamic',
      ReviewStatus:  currentStatus,
      CreatedBy:     this.CreatedBy,
    };

    if (reviewNameBase) payload.ReviewName = reviewNameBase;
    if (CommonFields.length > 0)  payload.CommonFields  = CommonFields;
    payload.LocationFields = LocationFields;
    if (documents.length > 0)     payload.Documents     = documents;

    this.reviewerService.SaveReviewData(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          const errorMessages = this.extractErrorMessages(response);
          if (errorMessages.length > 0) {
            this.errorPopupMessages = errorMessages;
            this.showErrorPopup = true;
            return;
          }

          if (currentStatus === 'Draft') {
            this.router.navigate(['/review-management'], {
              state: { draftSuccess: true, reviewTypeName: this.reviewTypeName }
            });
          } else {
            this.router.navigate(['/review-management'], {
              state: { submitSuccess: true, reviewTypeName: this.reviewTypeName }
            });
          }
        },
        error: (err) => {
          console.error('SaveReviewData error status:', err?.status, 'message:', err?.message, 'full:', err);
          this.isLoading = false;
        }
      });
  }

  private async saveGoLiveDynamic(currentStatus: string): Promise<void> {
    const folder = (this.form.ReviewName ?? '').trim();
    const parentReviewName = this.buildReviewName('');
    const singleOrMulti = this.locationCards.length === 1 ? 'Single Location' : 'Multiple Location';

    const yearOption = this.proposedYears.find(
      y => String(y.ProposedYear) === String(this.form.ProposedYear) ||
           y.DisplayName === String(this.form.ProposedYear)
    );
    const proposedYearId = yearOption?.ProposedYearId ?? '';

    const documents: ReviewDocument[] = await Promise.all(
      this.attachedFiles.map(async (file, index) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          DocSeq: index + 1,
          DocumentType: 'Go-Live Review Attachment',
          FileName: file.name,
          StorageFilePath: folder ? `${folder}/${file.name}` : file.name,
          FileSize: file.size,
          FileType: ext,
          Description: '',
          fileContent,
          UploadedBy: this.CreatedBy,
        };
      })
    );

    const nv = (v: string | undefined | null): string => (v ?? '').trim();

    const locationFields: any[] = [];
    this.locationCards.forEach(card => {
      const locationName = this.getCardPrimaryCity(card);
      const isParent = card.IsParentLocation ? 1 : 0;
      // Always include the primary card (even with no city/country) so the
      // SP JOIN returns a row for this review. Skip secondary cards that have
      // no data at all.
      if (!locationName && !nv(card.LocationCountry) && !card.IsParentLocation) return;
      [
        { FieldName: 'DeliveryCountry',      FieldValue: nv(card.LocationCountry), DataType: 'nvarchar' },
        { FieldName: 'LocationToBeReviewed', FieldValue: locationName,             DataType: 'nvarchar' },
        { FieldName: 'Quarter',              FieldValue: nv(card.Quarter),         DataType: 'nvarchar' },
        { FieldName: 'ProposedMonth',        FieldValue: nv(card.ProposedMonth),   DataType: 'nvarchar' },
        { FieldName: 'RCManager',            FieldValue: nv(card.RCManager),           DataType: 'nvarchar' },
        { FieldName: 'RCSupervisor',         FieldValue: nv(card.RCSupervisor),        DataType: 'nvarchar' },
        { FieldName: 'RCReviewer',           FieldValue: nv(card.RCReviewer),          DataType: 'nvarchar' },
        { FieldName: 'secondaryRCManager',   FieldValue: nv(card.SecondaryManager),    DataType: 'nvarchar' },
        { FieldName: 'secondaryRCSupervisor',FieldValue: nv(card.SecondarySupervisor), DataType: 'nvarchar' },
        { FieldName: 'secondaryRCReviewer',  FieldValue: nv(card.SecondaryReviewer),   DataType: 'nvarchar' },
      ].forEach(f => locationFields.push({ LocationName: locationName, IsParentLocation: isParent, ...f }));
    });

    const commonFields: any[] = [
      { FieldName: 'ReviewTypeID', FieldValue: '2', DataType: 'int' },
    ];
    if (proposedYearId !== '') {
      commonFields.push({ FieldName: 'ProposedYearId', FieldValue: String(proposedYearId), DataType: 'int' });
    }
    if (nv(this.form.MasterClientName))         commonFields.push({ FieldName: 'ClientName',              FieldValue: nv(this.form.MasterClientName),         DataType: 'nvarchar' });
    if (nv(this.form['D&TProjectName']))        commonFields.push({ FieldName: 'ProjectName',             FieldValue: nv(this.form['D&TProjectName']),        DataType: 'nvarchar' });
    if (nv(this.form.ContractNumber))           commonFields.push({ FieldName: 'ContractNumber',           FieldValue: nv(this.form.ContractNumber),           DataType: 'nvarchar' });
    if (nv(this.form.AdditionalContractNumber)) commonFields.push({ FieldName: 'AdditionalContractNumber', FieldValue: nv(this.form.AdditionalContractNumber), DataType: 'nvarchar' });
    if (nv(this.form.Market))                   commonFields.push({ FieldName: 'Market',                   FieldValue: nv(this.form.Market),                   DataType: 'nvarchar' });
    if (nv(this.form.MarketUnit))               commonFields.push({ FieldName: 'MarketUnit',               FieldValue: nv(this.form.MarketUnit),               DataType: 'nvarchar' });

    commonFields.push({ FieldName: 'SingleMultipleLocation', FieldValue: singleOrMulti,             DataType: 'nvarchar' });
    commonFields.push({ FieldName: 'TypeOfReview',           FieldValue: this.glTypeOfReview,       DataType: 'nvarchar' });
    if (nv(this.glGoLiveDate))           commonFields.push({ FieldName: 'GoLiveDate',               FieldValue: nv(this.glGoLiveDate),           DataType: 'nvarchar' });
    if (nv(this.glPreFwStartDate))       commonFields.push({ FieldName: 'PreGoLiveFWStartDate',     FieldValue: nv(this.glPreFwStartDate),       DataType: 'nvarchar' });
    if (nv(this.glPreFwEndDate))         commonFields.push({ FieldName: 'PreGoLiveFWEndDate',       FieldValue: nv(this.glPreFwEndDate),         DataType: 'nvarchar' });
    if (nv(this.glInterimCallDate))      commonFields.push({ FieldName: 'GLRRInterimCallDate',      FieldValue: nv(this.glInterimCallDate),      DataType: 'nvarchar' });
    if (nv(this.glPostFwStartDate))      commonFields.push({ FieldName: 'PostGoLiveFWStartDate',    FieldValue: nv(this.glPostFwStartDate),      DataType: 'nvarchar' });
    if (nv(this.glPostFwEndDate))        commonFields.push({ FieldName: 'PostGoLiveFWEndDate',      FieldValue: nv(this.glPostFwEndDate),        DataType: 'nvarchar' });
    if (nv(this.glPublishFinalReport))   commonFields.push({ FieldName: 'PublishFinalReport',       FieldValue: nv(this.glPublishFinalReport),   DataType: 'nvarchar' });
    if (nv(this.form.Comments))          commonFields.push({ FieldName: 'Comments',                  FieldValue: nv(this.form.Comments),          DataType: 'nvarchar' });

    const payload = {
      category: 'SaveReviewDynamic',
      ReviewName: parentReviewName,
      ReviewStatus: currentStatus,
      CreatedBy: this.CreatedBy,
      CommonFields: commonFields,
      LocationFields: locationFields,
      Documents: documents,
    };

    this.reviewerService.SaveReviewDynamic(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          const errorMessages = this.extractErrorMessages(response);
          if (errorMessages.length > 0) {
            this.errorPopupMessages = errorMessages;
            this.showErrorPopup = true;
            return;
          }
          if (currentStatus === 'Draft') {
            this.router.navigate(['/review-management'], {
              state: { draftSuccess: true, reviewTypeName: this.reviewTypeName }
            });
          } else {
            this.router.navigate(['/review-management'], {
              state: { submitSuccess: true, reviewTypeName: this.reviewTypeName }
            });
          }
        },
        error: (err) => {
          this.isLoading = false;
          console.error('SaveReviewDynamic error status:', err?.status, 'message:', err?.message, 'full:', err);
        }
      });
  }

  private async saveVariantReviewDynamic(currentStatus: string): Promise<void> {
    const isSpr = this.isSpecialProject;
    const folder = (this.form.ReviewName ?? '').trim();
    const parentReviewName = this.buildReviewName('');
    const singleOrMulti = this.locationCards.length === 1 ? 'Single Location' : 'Multiple Location';

    const yearOption = this.proposedYears.find(
      y => String(y.ProposedYear) === String(this.form.ProposedYear) ||
           y.DisplayName === String(this.form.ProposedYear)
    );
    const proposedYearId = yearOption?.ProposedYearId ?? '';

    const documentType = isSpr ? 'Special Project Review Attachment' : 'On-Demand Review Attachment';
    const documents: ReviewDocument[] = await Promise.all(
      this.attachedFiles.map(async (file, index) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          DocSeq: index + 1,
          DocumentType: documentType,
          FileName: file.name,
          StorageFilePath: folder ? `${folder}/${file.name}` : file.name,
          FileSize: file.size,
          FileType: ext,
          Description: '',
          fileContent,
          UploadedBy: this.CreatedBy,
        };
      })
    );

    const nv = (v: string | undefined | null): string => (v ?? '').trim();

    const locationFields: any[] = [];
    this.locationCards.forEach(card => {
      const locationName = this.getCardPrimaryCity(card);
      const isParent = card.IsParentLocation ? 1 : 0;
      if (!locationName && !nv(card.LocationCountry) && !card.IsParentLocation) return;
      [
        { FieldName: 'DeliveryCountry',       FieldValue: nv(card.LocationCountry),    DataType: 'nvarchar' },
        { FieldName: 'LocationToBeReviewed',  FieldValue: locationName,                DataType: 'nvarchar' },
        { FieldName: 'Quarter',               FieldValue: nv(card.Quarter),            DataType: 'nvarchar' },
        { FieldName: 'ProposedMonth',         FieldValue: nv(card.ProposedMonth),      DataType: 'nvarchar' },
        { FieldName: 'RCManager',             FieldValue: nv(card.RCManager),          DataType: 'nvarchar' },
        { FieldName: 'RCSupervisor',          FieldValue: nv(card.RCSupervisor),       DataType: 'nvarchar' },
        { FieldName: 'RCReviewer',            FieldValue: nv(card.RCReviewer),         DataType: 'nvarchar' },
        { FieldName: 'secondaryRCManager',    FieldValue: nv(card.SecondaryManager),   DataType: 'nvarchar' },
        { FieldName: 'secondaryRCSupervisor', FieldValue: nv(card.SecondarySupervisor),DataType: 'nvarchar' },
        { FieldName: 'secondaryRCReviewer',   FieldValue: nv(card.SecondaryReviewer),  DataType: 'nvarchar' },
      ].forEach(f => locationFields.push({ LocationName: locationName, IsParentLocation: isParent, ...f }));
    });

    const reviewTypeId = isSpr ? '4' : '3';
    const commonFields: any[] = [
      { FieldName: 'ReviewTypeID', FieldValue: reviewTypeId, DataType: 'int' },
    ];
    if (proposedYearId !== '') {
      commonFields.push({ FieldName: 'ProposedYearId', FieldValue: String(proposedYearId), DataType: 'int' });
    }
    if (nv(this.form.MasterClientName))         commonFields.push({ FieldName: 'ClientName',              FieldValue: nv(this.form.MasterClientName),         DataType: 'nvarchar' });
    if (nv(this.form['D&TProjectName']))        commonFields.push({ FieldName: 'ProjectName',             FieldValue: nv(this.form['D&TProjectName']),        DataType: 'nvarchar' });
    if (nv(this.form.ContractNumber))           commonFields.push({ FieldName: 'ContractNumber',           FieldValue: nv(this.form.ContractNumber),           DataType: 'nvarchar' });
    if (nv(this.form.AdditionalContractNumber)) commonFields.push({ FieldName: 'AdditionalContractNumber', FieldValue: nv(this.form.AdditionalContractNumber), DataType: 'nvarchar' });
    if (nv(this.form.Market))                   commonFields.push({ FieldName: 'Market',                   FieldValue: nv(this.form.Market),                   DataType: 'nvarchar' });
    if (nv(this.form.MarketUnit))               commonFields.push({ FieldName: 'MarketUnit',               FieldValue: nv(this.form.MarketUnit),               DataType: 'nvarchar' });

    commonFields.push({ FieldName: 'SingleMultipleLocation', FieldValue: singleOrMulti, DataType: 'nvarchar' });
    if (isSpr) {
      commonFields.push({ FieldName: 'TypeOfProject', FieldValue: nv(this.specialProjectTypeOfProject), DataType: 'nvarchar' });
    }
    if (nv(this.onDemandFwStartDate)) commonFields.push({ FieldName: 'FieldworkStartDate', FieldValue: nv(this.onDemandFwStartDate), DataType: 'nvarchar' });
    if (nv(this.onDemandFwEndDate))   commonFields.push({ FieldName: 'FieldworkEndDate',   FieldValue: nv(this.onDemandFwEndDate),   DataType: 'nvarchar' });
    if (nv(this.form.Comments))       commonFields.push({ FieldName: 'Comments',            FieldValue: nv(this.form.Comments),       DataType: 'nvarchar' });

    const payload = {
      category: 'SaveReviewDynamic',
      ReviewName: parentReviewName,
      ReviewStatus: currentStatus,
      CreatedBy: this.CreatedBy,
      CommonFields: commonFields,
      LocationFields: locationFields,
      Documents: documents,
    };

    console.log('SaveVariantReviewDynamic payload:', { params: payload });

    this.reviewerService.SaveReviewDynamic(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          const errorMessages = this.extractErrorMessages(response);
          if (errorMessages.length > 0) {
            this.errorPopupMessages = errorMessages;
            this.showErrorPopup = true;
            return;
          }
          if (currentStatus === 'Draft') {
            this.router.navigate(['/review-management'], {
              state: { draftSuccess: true, reviewTypeName: this.reviewTypeName }
            });
          } else {
            this.router.navigate(['/review-management'], {
              state: { submitSuccess: true, reviewTypeName: this.reviewTypeName }
            });
          }
        },
        error: (err) => {
          this.isLoading = false;
          console.error('SaveVariantReviewDynamic error status:', err?.status, 'message:', err?.message, 'full:', err);
        }
      });
  }

  // Returns any ErrorMessage values from the API response (e.g. duplicate review name).
  // An empty array means the save succeeded.
  private extractErrorMessages(response: any): string[] {
    if (!Array.isArray(response)) return [];
    return response
      .map(item => {
        if (item?.ErrorMessage?.trim()) return item.ErrorMessage as string;
        const msg: string = item?.Message ?? '';
        if (msg.trim().toLowerCase().startsWith('review already exists')) {
          return 'A review with this name already exists. Please update the review details to generate a unique Review Name.';
        }
        return '';
      })
      .filter((msg): msg is string => msg.trim().length > 0);
  }

  closeErrorPopup() {
    this.showErrorPopup = false;
    this.errorPopupMessages = [];
  }

  private uploadAttachedFiles(): void {
    if (this.attachedFiles.length === 0) return;
    const folder = (this.form.ReviewName ?? '').trim();
    if (!folder) return;

    Promise.all(
      this.attachedFiles.map(file =>
        this.readFileAsBase64(file).then(fileContent => {
          return {
            fileName: `${folder}/${file.name}`,
            fileContent
          };
        })
      )
    )
      .then(files => {
        this.reviewerService.uploadMultipleFiles(files)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => { /* upload succeeded */ },
            error: err => console.error('UploadMultipleFiles error status:', err?.status, 'message:', err?.message, 'full:', err)
          });
      })
      .catch(err => console.error('Failed to read attached files for upload:', err));
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  resetForm() {
    this.form = createDefaultStandardReviewForm();
    this.locationCards = [this.newEmptyCard(true)];
    this.locationCardErrors = [{}];
    this.cardCities = [[]];
    this.cardCountrySearchTerms = [''];
    this.cardCitySearchTerms = [''];
    this.cardCountryDropdownOpenIdx = null;
    this.cardCityDropdownOpenIdx = null;
    this.availableCities = [];
    this.attachedFiles = [];
    this.attachmentErrors = [];
    this.formErrors = {};
    this.reviewNameDuplicateError = '';
    this.glTypeOfReview = 'pre-post';
    this.glGoLiveDate = '';
    this.glPreFwStartDate = '';
    this.glPreFwEndDate = '';
    this.glInterimCallDate = '';
    this.glPostFwStartDate = '';
    this.glPostFwEndDate = '';
    this.glPublishFinalReport = '';
    this.glFormErrors = {};
    this.onDemandFwStartDate = '';
    this.onDemandFwEndDate = '';
    this.onDemandFormErrors = {};
    this.specialProjectTypeOfProject = '';
    this.specialProjectFormErrors = {};
    this.typeOfProjectDropdownOpen = false;
    this.typeOfProjectSearchTerm = '';
    if (this.useGoLiveLayout) {
      this.locationCards = [this.createEmptyGlCard(true)];
      this.goLiveCardEditMode = [true];
    }
    this.clientAccountLeadError = '';
    this.operationAccountLeadError = '';
    this.clientSearchTerm = '';
    this.projectSearchTerm = '';
    this.countrySearchTerm = '';
    this.locationSearchTerm = '';
    this.dtProjectNames = [];
    this.contractNumbers = [];
    setTimeout(() => this.adjustReviewNameHeight(), 0);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  cancelForm() {
    if (!this.isFormDirty) {
      this.router.navigate(['/planning']);
      return;
    }
    this.showCancelConfirm = true;
  }

  confirmCancel() {
    this.showCancelConfirm = false;
    this.resetForm();
    this.router.navigate(['/planning']);
  }

  dismissCancelConfirm() {
    this.showCancelConfirm = false;
  }
}
